<template>
  <div class="interest-calculation-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>利息计算</span>
      </div>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">新增利息计算</el-button>
      </div>
    </div>

    <!-- 搜索区域（与收支台账保持一致） -->
    <div class="filter-container">
      <el-input
        v-model="searchParams.contractName"
        placeholder="合同名称"
        style="width: 200px"
        class="filter-item"
        clearable
      />
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="截止日期起"
        end-placeholder="截止日期止"
        value-format="yyyy-MM-dd"
        class="filter-item"
        style="width: 240px"
      />
      <el-button type="primary" icon="el-icon-search" class="filter-item" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh" class="filter-item" @click="handleReset">重置</el-button>
    </div>

    <!-- 利息计算记录列表 -->
    <el-table
      :data="displayList"
      border
      style="width: 100%"
      v-loading="loading"
    >
      <el-table-column label="合同名称" min-width="180" align="center" prop="contractName" show-overflow-tooltip />
      <el-table-column label="需求方名称" min-width="140" align="center" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.demandUnitNames || scope.row.demandUnitName || '-' }}</template>
      </el-table-column>
      <el-table-column label="供应商名称" min-width="140" align="center" prop="supplierUnitName" show-overflow-tooltip />
      <el-table-column label="项目名称" min-width="140" align="center" prop="projectName" show-overflow-tooltip />
      <el-table-column label="利息截止日期" width="130" align="center" prop="cutoffDate" />
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="getStatusTagType(scope.row.status)" size="small">{{ getStatusText(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="结算总额" width="140" align="center">
        <template slot-scope="scope">
          <span class="amount-text">{{ formatAmount(scope.row.settlementTotalAmount) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="利息总额" width="140" align="center">
        <template slot-scope="scope">
          <span class="interest-text">{{ formatAmount(scope.row.interestTotalAmount) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170" align="center">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="210" align="center" fixed="right">
        <template slot-scope="scope">
          <div class="operate-btns">
            <el-button type="primary" size="mini" icon="el-icon-view" @click="viewDetail(scope.row)">查看明细</el-button>
            <el-button
              v-if="scope.row.status === 'DRAFT' || scope.row.status === 'ACTIVE'"
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { queryInterestCalculations, deleteInterestCalculation } from '@/api/modules/interest-calculation'

export default {
  name: 'InterestManagementList',
  data() {
    return {
      loading: false,
      dateRange: null,
      searchParams: {
        contractName: '',
        startDate: '',
        endDate: ''
      },
      pagination: {
        page: 1,
        size: 10,
        total: 0
      },
      displayList: []
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    async loadList() {
      this.loading = true
      try {
        const params = {
          page: (this.pagination.page || 1) - 1,
          size: this.pagination.size,
          contractName: this.searchParams.contractName || undefined,
          startDate: this.searchParams.startDate || undefined,
          endDate: this.searchParams.endDate || undefined
        }
        const response = await queryInterestCalculations(params)
        const res = response.data
        if (res && res.success) {
          const pageData = res.data
          this.displayList = (pageData && pageData.content) ? pageData.content : []
          this.pagination.total = (pageData && pageData.totalElements) ? pageData.totalElements : 0
        } else {
          this.$message.error((res && res.message) || '加载列表失败')
        }
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || error.message || '加载列表失败'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadList()
    },
    handleReset() {
      this.searchParams = { contractName: '', startDate: '', endDate: '' }
      this.dateRange = null
      this.pagination.page = 1
      this.loadList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.page = 1
      this.loadList()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadList()
    },
    handleCreate() {
      this.$router.push('/finance/interest-calculation-create')
    },
    viewDetail(row) {
      this.$router.push(`/finance/interest-calculation-detail/${row.id}`)
    },
    handleDelete(row) {
      this.$confirm('确定要删除该利息计算记录吗？删除后无法恢复。', '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteInterestCalculation(row.id)
          const data = res.data
          if (data && data.success) {
            this.$message.success('删除成功')
            this.loadList()
          } else {
            this.$message.error((data && data.message) || '删除失败')
          }
        } catch (error) {
          const msg = (error.response && error.response.data && error.response.data.message) || error.message || '删除失败'
          this.$message.error(msg)
        }
      }).catch(() => {})
    },
    formatDateTime(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getStatusText(status) {
      const map = { DRAFT: '待审核', ACTIVE: '待确认', EXECUTING: '已确认' }
      return map[status] || status || '-'
    },
    getStatusTagType(status) {
      const map = { DRAFT: 'info', ACTIVE: 'warning', EXECUTING: 'success' }
      return map[status] || 'info'
    }
  },
  watch: {
    dateRange(val) {
      if (val && val.length === 2) {
        this.searchParams.startDate = val[0]
        this.searchParams.endDate = val[1]
      } else {
        this.searchParams.startDate = ''
        this.searchParams.endDate = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.interest-calculation-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.page-header {
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;

    .title-mark {
      width: 4px;
      height: 18px;
      background: #409eff;
      margin-right: 10px;
    }
  }
}

/* 与收支台账页面保持一致 */
.filter-container {
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 20px;
}

.filter-item {
  margin-bottom: 10px;
  margin-right: 10px;
}

.el-table {
  margin-bottom: 20px;
}

.amount-text {
  color: #303133;
  font-weight: 500;
}

.interest-text {
  color: #e6a23c;
  font-weight: 600;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;
}

.operate-btns {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 6px;
}
</style>

<style lang="scss">
.interest-calculation-container {
  .el-table__fixed-right {
    border-left: 1px solid #ebeef5;
  }
  .el-table__fixed-right-patch {
    border-left: 1px solid #ebeef5;
  }
}
</style>
